import React, { useState } from 'react'
import { Box, Container, Typography, Button, Paper, Alert, Snackbar, CircularProgress } from '@mui/material'
import { EmailOutlined } from '@mui/icons-material'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'

const VerifyEmail = () => {
  const { currentUser, resendVerificationEmail, logout } = useAuth()
  const [loading, setLoading] = useState(false)
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' })
  const navigate = useNavigate()

  const handleResendEmail = async () => {
    setLoading(true)
    try {
      const result = await resendVerificationEmail()
      if (result.success) {
        setNotification({
          open: true,
          message: result.message,
          severity: 'success',
        })
      } else {
        setNotification({
          open: true,
          message: result.error || 'Failed to send verification email',
          severity: 'error',
        })
      }
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'An error occurred',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    window.location.href = 'https://app.indabo.com'
  }

  const handleSignOut = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      setNotification({
        open: true,
        message: error.message || 'Failed to sign out',
        severity: 'error',
      })
    }
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false })
  }

  if (!currentUser) {
    navigate('/login')
    return null
  }

  return (
    <Container maxWidth='sm'>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <EmailOutlined
            sx={{
              fontSize: 64,
              color: 'primary.main',
              mb: 2,
            }}
          />

          <Typography variant='h4' component='h1' gutterBottom align='center'>
            Verify Your Email
          </Typography>

          <Typography variant='body1' align='center' paragraph>
            We've sent a verification email to:
          </Typography>

          <Typography variant='body1' fontWeight='bold' align='center' paragraph>
            {currentUser.email}
          </Typography>

          <Typography variant='body1' align='center' paragraph>
            Please check your inbox and click the verification link to activate your account. If you don't see the email, check your spam folder.
          </Typography>

          <Box sx={{ mt: 3, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant='contained'
              color='primary'
              fullWidth
              onClick={handleResendEmail}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </Button>

            <Button variant='outlined' color='primary' fullWidth onClick={handleRefresh}>
              I've Verified My Email. Take me to the app.
            </Button>

            <Button variant='text' color='inherit' fullWidth onClick={handleSignOut}>
              Sign Out
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={notification.open}
          autoHideDuration={6000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseNotification} severity={notification.severity}>
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  )
}

export default VerifyEmail
